import { Stack, Group, Text, Badge, Box } from '@repletoryreach/sdk-ui';
import { formatInteger, formatMinor } from '../../../../lib/format';
import { TenantName } from '../../../../components/TenantName';
import type { UsageRow } from './UsageTable';
import { useT } from '../../../../i18n/I18nProvider';

interface Props {
  records: UsageRow[];
  onSelect: (row: UsageRow) => void;
}

/** Vertical list view: one row per tenant and currency. Rows open the ledger. */
export function UsageList({ records, onSelect }: Props) {
  const t = useT();
  return (
    <Stack gap="xs">
      {records.map((r) => (
        <button key={r.id} type="button" className="rr-list-row" onClick={() => onSelect(r)}>
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <TenantName id={r.organisation_id} />
                <Badge size="xs" variant="light">{r.currency}</Badge>
              </Group>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t(r.events === 1 ? 'billing.usage.list.eventsOne' : 'billing.usage.list.eventsOther', { count: formatInteger(r.events) })}
              </Text>
            </Stack>
            {/* Debits and credits mean opposite things, so they are not the same
                colour: the two figures used to render identically. */}
            <Box style={{ minWidth: 120, textAlign: 'right' }}>
              <Text role="bodySmall" fw={700} c="var(--rr-color-error)">{formatMinor(r.debit_minor, r.currency)}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.usage.list.debit')}</Text>
            </Box>
            <Box style={{ minWidth: 120, textAlign: 'right' }}>
              <Text role="bodySmall" fw={700} c="var(--rr-color-success)">{formatMinor(r.credit_minor, r.currency)}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.usage.list.credit')}</Text>
            </Box>
          </Group>
        </button>
      ))}
    </Stack>
  );
}
